import { useMutation, useQueryClient } from "react-query";
import toast from "react-hot-toast";

interface MutationConfig {
  saveFn: (payload: any) => Promise<any>;
  submitFn: (assessmentId: string) => Promise<any>;
  returnToDraftFn?: (assessmentId: string) => Promise<any>;
  invalidateKeys: string[][];
  // Optional override for the save mutation — when set, only these keys are
  // invalidated after a draft save. Useful for pages that own the canonical
  // latest-assessment query and would otherwise refetch + reset local state
  // (e.g. losing scroll position) on every save.
  saveInvalidateKeys?: string[][];
  entityLabel?: string;
}

interface MutationCallbacks {
  onSaveSuccess?: (response: any) => void;
  onSubmitSuccess?: (response: any) => void;
  onReturnToDraftSuccess?: (response: any) => void;
}

export function useAssessmentMutations(
  config: MutationConfig,
  callbacks?: MutationCallbacks,
) {
  const queryClient = useQueryClient();
  const label = config.entityLabel ?? "assessment";

  const invalidateAll = () => {
    for (const key of config.invalidateKeys) {
      queryClient.invalidateQueries(key);
    }
  };

  const invalidateOnSave = () => {
    const keys = config.saveInvalidateKeys ?? config.invalidateKeys;
    for (const key of keys) {
      queryClient.invalidateQueries(key);
    }
  };

  const saveMutation = useMutation(config.saveFn, {
    onSuccess: (response: any) => {
      invalidateOnSave();
      toast.success(`${label} draft saved`);
      callbacks?.onSaveSuccess?.(response);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || `Failed to save ${label}`);
    },
  });

  const submitMutation = useMutation(config.submitFn, {
    onSuccess: (response: any) => {
      invalidateAll();
      toast.success(`${label} submitted`);
      callbacks?.onSubmitSuccess?.(response);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || `Failed to submit ${label}`);
    },
  });

  const returnToDraftMutationResult = useMutation(
    (assessmentId: string) => {
      if (!config.returnToDraftFn) {
        return Promise.reject(new Error(`Cannot return ${label} to draft`));
      }
      return config.returnToDraftFn(assessmentId);
    },
    {
      onSuccess: (response: any) => {
        invalidateAll();
        toast.success(`${label} returned to draft`);
        callbacks?.onReturnToDraftSuccess?.(response);
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error || `Failed to return ${label} to draft`,
        );
      },
    },
  );

  const returnToDraftMutation = config.returnToDraftFn
    ? returnToDraftMutationResult
    : null;

  return {
    saveMutation,
    submitMutation,
    returnToDraftMutation,
    isSaving: saveMutation.isLoading,
    isSubmitting: submitMutation.isLoading,
    isReturning: returnToDraftMutation?.isLoading ?? false,
  };
}
